import { useState, useEffect, useCallback } from "react";
import { Loader2, Mail, RotateCcw } from "lucide-react";
import { rowLine, rowAction, rowBlocked, reissuePayload } from "./inviteQueue";

// NEEDS SENDING (2026-11-02). Every open invite with no send recorded, for someone not already in. A live one is read
// and sent here, by the same press as the one after minting: the hash of what was read goes back, a stale view is
// refused, two presses are one send, and only the approver can press. A lapsed one has to be re-issued first.
const API_URL = process.env.REACT_APP_BACKEND_URL;
const box = { border: "1px solid #ddd", borderRadius: 6, padding: 12, marginTop: 8 };

async function call(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: body ? "POST" : "GET", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data.detail;
    throw new Error((d && d.message) || (typeof d === "string" && d) || `Request failed (${res.status})`);
  }
  return data;
}

function Row({ row, viewerCanSend, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  async function read() {
    setNote("");
    try {
      setPreview(await call(`/api/admin/invites/${row.token}/preview`));
    } catch (e) {
      setNote(e.message);
    }
  }

  async function act(run) {
    setBusy(true);
    setNote("");
    try {
      await run();
      onChanged();
    } catch (e) {
      setNote(e.message);
    }
    setBusy(false);
  }

  const send = () => act(async () => {
    const out = await call(`/api/admin/invites/${row.token}/send`, { message_hash: preview.message_hash });
    if (out.verdict !== "sent") throw new Error(`Not sent: ${out.reason || "unknown error"}`);
  });
  const reissue = () => act(() => call("/api/admin/invites/fitter/reissue", reissuePayload(row)));
  const action = rowAction(row);

  return (
    <div style={box} data-testid={`needs-sending-${row.email}`}>
      <b>{row.name || row.email}</b> ({row.email}){row.group ? `, Group ${row.group}` : ""}
      <div className="pilot-when">{rowLine(row)}</div>
      {rowBlocked(row) && <div className="pilot-when">{rowBlocked(row)}</div>}
      {action === "reissue" && (
        <button type="button" className="btn-secondary" onClick={reissue} disabled={busy}>
          {busy ? <Loader2 size={14} className="pilot-spin" /> : <RotateCcw size={14} />} Re-issue the link
        </button>
      )}
      {action === "send" && !preview && (
        <button type="button" className="btn-secondary" onClick={read}><Mail size={14} /> Read it, then send</button>
      )}
      {action === "send" && preview && (
        <div>
          <p><b>To:</b> {preview.name} ({preview.to.join(", ")})</p>
          <p><b>Subject:</b> {preview.subject}</p>
          <iframe title={`Invite to ${row.email}`} sandbox="" srcDoc={preview.html}
                  style={{ width: "100%", height: 320, border: "1px solid #ddd" }} />
          {viewerCanSend
            ? (
              <button type="button" className="btn-secondary" onClick={send} disabled={busy}>
                {busy ? <Loader2 size={14} className="pilot-spin" /> : <Mail size={14} />} Send to {row.email}
              </button>
            )
            : <p>Only the approver can send an invite.</p>}
        </div>
      )}
      {note && <p role="status">{note}</p>}
    </div>
  );
}

export default function NeedsSending({ onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await call("/api/admin/invites/needs-sending"));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changed = () => {
    load();
    if (onChanged) onChanged();
  };
  const rows = (data && data.rows) || [];

  return (
    <div className="pilot-group" data-testid="needs-sending">
      <h3>Invites needing a send</h3>
      {error && <p role="status">Could not read this list: {error}</p>}
      {!error && data && rows.length === 0 && <p>Nothing needs sending.</p>}
      {rows.map((r) => (
        <Row key={r.token} row={r} viewerCanSend={!!data.viewer_can_send} onChanged={changed} />
      ))}
    </div>
  );
}
